/**
 * React Query hooks for Channel Integration CRUD.
 *
 * Follows the same pattern as useMCPServers.ts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  ChannelIntegration,
  ChannelIntegrationCreate,
  ChannelIntegrationUpdate,
  ChannelListResponse,
  ChannelVerifyResult,
  JwksRefreshResult,
} from '../types';

const CHANNELS_KEY = 'channels';

// =============================================================================
// Query Hooks
// =============================================================================

export function useChannels(page = 1, pageSize = 20) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [CHANNELS_KEY, tenantId, page, pageSize],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      const { data } = await apiClient.get<ChannelListResponse>(`/api/v1/channels?${params}`);
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useChannel(channelId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [CHANNELS_KEY, tenantId, channelId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<ChannelIntegration>(`/api/v1/channels/${channelId}`);
      return data;
    },
    enabled: !!tenantId && !!channelId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateChannel() {
  const { currentTenant } = useTenant();
  const qc = useQueryClient();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (channelData: ChannelIntegrationCreate) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<ChannelIntegration>('/api/v1/channels', channelData);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHANNELS_KEY, tenantId] });
    },
  });
}

export function useUpdateChannel(channelId: string) {
  const { currentTenant } = useTenant();
  const qc = useQueryClient();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (channelData: ChannelIntegrationUpdate) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.patch<ChannelIntegration>(`/api/v1/channels/${channelId}`, channelData);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHANNELS_KEY, tenantId] });
    },
  });
}

export function useDeleteChannel() {
  const { currentTenant } = useTenant();
  const qc = useQueryClient();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (channelId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      await apiClient.delete(`/api/v1/channels/${channelId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHANNELS_KEY, tenantId] });
    },
  });
}

export function useVerifyChannel() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (channelId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<ChannelVerifyResult>(`/api/v1/channels/${channelId}/verify`, {});
      return data;
    },
  });
}

export function useRefreshJwks() {
  const { currentTenant } = useTenant();
  const qc = useQueryClient();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (channelId: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<JwksRefreshResult>(
        `/api/v1/channels/${channelId}/refresh-jwks`,
        {},
      );
      return data;
    },
    onSuccess: (_data, channelId) => {
      qc.invalidateQueries({ queryKey: [CHANNELS_KEY, tenantId, channelId] });
    },
  });
}

// =============================================================================
// Sprint 88 — Meeting Assistant + Proactive Messaging hooks
// =============================================================================

export interface MeetingSessionSummary {
  id: string;
  teams_meeting_id: string;
  organizer_user_id: string;
  started_at: string | null;
  ended_at: string | null;
  transcript_status:
    | 'pending'
    | 'processing'
    | 'done'
    | 'failed'
    | 'no_transcript';
  summary_sent: boolean;
  action_items_count: number;
}

export interface MeetingSessionDetail extends MeetingSessionSummary {
  summary_text: string | null;
  error_detail: string | null;
}

export function useMeetingSessions(channelId: string, limit = 50) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [CHANNELS_KEY, tenantId, channelId, 'meetings', limit],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const params = new URLSearchParams({ limit: String(limit) });
      const { data: responseData } = await apiClient.get<{ data: MeetingSessionSummary[] }>(
        `/api/v1/channels/${channelId}/meetings?${params}`,
      );
      return responseData;
    },
    enabled: !!tenantId && !!channelId,
  });
}

export function useMeetingSession(channelId: string, meetingSessionId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [CHANNELS_KEY, tenantId, channelId, 'meetings', meetingSessionId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data: responseData } = await apiClient.get<{ data: MeetingSessionDetail }>(
        `/api/v1/channels/${channelId}/meetings/${meetingSessionId}`,
      );
      return responseData;
    },
    enabled: !!tenantId && !!channelId && !!meetingSessionId,
  });
}

export interface ProactiveSendPayload {
  external_user_hash: string;
  message_text: string;
  template_slug?: string;
}

export interface ProactiveSendResult {
  message_id: string;
  status:
    | 'queued'
    | 'blocked_consent'
    | 'blocked_rate_limit'
    | 'blocked_window';
}

/**
 * @deprecated Use `useSendProactiveBatch` (ADR-035 DEC-CH-16). Backed by the
 * legacy `POST /channels/{id}/proactive` endpoint kept for backwards compat.
 */
export function useSendProactiveMessage(channelId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (payload: ProactiveSendPayload) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data: responseData } = await apiClient.post<{ data: ProactiveSendResult }>(
        `/api/v1/channels/${channelId}/proactive`,
        payload,
      );
      return responseData;
    },
  });
}

// ADR-035 DEC-CH-16 batch contract ---------------------------------------

export type RecipientSelector = 'user_hash_list' | 'group_id' | 'all';

export interface ProactiveBatchPayload {
  recipient_selector: RecipientSelector;
  text: string;
  user_hashes?: string[];
  group_id?: string;
  template_id?: string;
  metadata?: Record<string, unknown>;
}

export interface ProactiveBatchBlocked {
  external_user_hash?: string;
  status: string;
  error?: string;
}

export interface ProactiveBatchResult {
  queue_id: string;
  count_queued: number;
  count_blocked: number;
  count_failed: number;
  message_ids: string[];
  blocked: ProactiveBatchBlocked[];
}

export function useSendProactiveBatch(channelId: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (payload: ProactiveBatchPayload) => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data: responseData } = await apiClient.post<{ data: ProactiveBatchResult }>(
        `/api/v1/channels/${channelId}/send`,
        payload,
      );
      return responseData;
    },
  });
}
